/* eslint-disable jsx-a11y/alt-text */
// TODO: Update jsx-a11y
import PropTypes from 'prop-types';
import React from 'react';
import { renderRichText } from 'gatsby-source-contentful/rich-text';

import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types';
import { GatsbyImage } from 'gatsby-plugin-image';

import gtmData from 'data/gtm';
import toPaschalCase from 'utils/toPaschalCase';
import Link from 'components/elements/Link';
import SocialLink from 'components/elements/SocialLink';
import ImageContent from 'components/elements/ImageContent';
import Accordion from 'components/elements/Accordion';

const splitElementsWithLineBreak = elements => {
  if (elements.length <= 1) return elements;
  return elements.reduce((children, element, i) => [...children, i > 0 && <br key={i} />, element], []);
};

const createColoredSpan = element => {
  if (typeof element !== 'string') return element;

  const colorFormat = /\[.+?\]\(#.+?\)/; // '[text to display](#color-hex)'
  const colorFormatGlobal = /(\[[^[\]]+?\]\(#([a-zA-Z0-9]{3}|[a-zA-Z0-9]{6})\))/g; // '[text to display](#color-hex)' global
  const colorFormatTexts = /\[(.+?)\]\((#.+?)\)/; // ['[text to display](#color-hex)', 'text to display', '#color-hex']

  const groups = (element.match(colorFormatGlobal) || [])
    .map(segment => segment.match(colorFormatTexts))
    .map(([, textToDisplay, color], i) => (
      <span key={i} style={{ color }}>
        {textToDisplay}
      </span>
    ));

  return element.split(colorFormat).reduce((children, segment, i) => [...children, segment, groups[i]], []);
};

const options = {
  renderText: text => {
    if (text.includes('<table>')) return <div className="table-content" dangerouslySetInnerHTML={{ __html: text }} />;

    const lines = text.includes('\n') ? text.split('\n') : [text];

    // Replace all occurrences of \n with <br/> - Shift+Enter creates \n in Contentful
    const textWithLineBreaks = splitElementsWithLineBreak(lines);

    // Replace all [text to display](#color-hex) with <span style={{ color: #color-hex }}>text to display</span>
    return textWithLineBreaks.map(createColoredSpan);
  },

  // Use code blocks to render iframes
  renderMark: {
    [MARKS.CODE]: embedded => {
      const content = embedded?.[0][0]?.replace?.(/[“”]/g, '"');
      const isIframe = content?.indexOf('iframe') !== -1;

      if (content === '') return null;

      return <span className={isIframe ? 'rt-iframe-wrapper' : null} dangerouslySetInnerHTML={{ __html: content }} />;
    },
  },
  renderNode: {
    [BLOCKS.EMBEDDED_ASSET]: node => {
      const imageFields = node?.data?.target;

      if (!imageFields) return null;

      if (imageFields.gatsbyImageData)
        return <GatsbyImage image={imageFields.gatsbyImageData} alt={imageFields.title || ''} />;

      return <img src={imageFields.file?.url} title={imageFields.title} loading="lazy" />;
    },
    [BLOCKS.EMBEDDED_ENTRY]: node => {
      const component = node?.data?.target?.__typename;
      const fields = node?.data?.target;

      const id = fields?.id;
      const title = fields?.title;
      const heading = fields?.heading;
      const contentRt = fields?.contentRt;
      const imageFields = fields?.image;
      const socialLinkType = fields?.strType?.name;
      const socialIcon = fields?.socialIcon;
      const url = fields?.url;
      const urlSuffix = fields?.urlSuffix;
      const isButton = fields?.isButton;
      const hasBackground = fields?.hasBackground;
      const customButtonBackgroundColor = fields?.customButtonBackgroundColor;
      const customButtonTextColor = fields?.customButtonTextColor;
      const useBoldButtonText = fields?.useBoldButtonText;
      const contentTextPosition = fields?.contentTextPosition;
      const textAlignement = fields?.textAlignement;
      const isOpenedByDefault = fields?.isOpenedByDefault;

      switch (component) {
        case 'ContentfulElementLink':
          return (
            <div className="custom-content-link-wrapper">
              <Link
                title={title}
                url={url}
                isButton={isButton}
                hasBackground={hasBackground}
                customButtonBackgroundColor={customButtonBackgroundColor}
                customButtonTextColor={customButtonTextColor}
                useBoldButtonText={useBoldButtonText}
              />
            </div>
          );
        case 'ContentfulElementSocialLink':
          return (
            <SocialLink
              type={socialLinkType}
              url={url}
              urlSuffix={urlSuffix}
              socialIcon={socialIcon}
              textColor="#266ff2"
            />
          );
        case 'ContentfulElementImageContent':
          return (
            <ImageContent
              title={title}
              image={imageFields}
              contentRt={contentRt}
              contentTextPosition={contentTextPosition}
              textAlignement={textAlignement}
            />
          );
        case 'ContentfulElementAccordion':
          return (
            <Accordion
              key={id}
              title={heading}
              content={contentRt}
              gtmTrackId={`${gtmData.faqToggleClass}-${toPaschalCase(heading)}`}
              gtmClassName={gtmData.faqToggleClass}
              isOpenedByDefault={isOpenedByDefault}
            />
          );
        default:
          return null;
      }
    },
    [INLINES.EMBEDDED_ENTRY]: node => {
      const component = node?.data?.target?.__typename;
      const fields = node?.data?.target;

      const title = fields?.title;

      const socialLinkType = fields?.strType?.name;
      const socialIcon = fields?.socialIcon;
      const url = fields?.url;

      switch (component) {
        case 'ContentfulElementLink':
          return <Link title={title} url={url} />;
        case 'ContentfulElementSocialLink':
          return <SocialLink type={socialLinkType} url={url} socialIcon={socialIcon} isInline />;
        default:
          return null;
      }
    },
    [INLINES.ENTRY_HYPERLINK]: node => {
      const content = node?.content;
      const title = content ? content[0]?.value : node?.data?.target?.title;
      const url = node?.data?.target?.url || node?.data?.target?.slug;

      const isInternal = typeof window !== 'undefined' && (url.includes(window.location.host) || /^\/(?!\/)/.test(url)); // check if link is internal if window is defined

      // Ensure internal links can be opened in a new tab
      if (!isInternal)
        return (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {title}
          </a>
        );

      return <Link title={title} url={url} />;
    },
    [INLINES.HYPERLINK]: node => {
      const url = node?.data?.uri;
      const title = node.content ? node.content[0]?.value : node?.data?.target?.title;
      const hasTel = url.startsWith('tel:');
      const isTel = hasTel || url.match(/^\d/);

      // Check if starts with number
      if (isTel) {
        const urlNumber = hasTel ? url : `tel:${url}`;
        return <a href={urlNumber}>{title}</a>;
      }

      const isInternal = typeof window !== 'undefined' && (url.includes(window.location.host) || /^\/(?!\/)/.test(url)); // check if link is internal if window is defined

      if (!isInternal)
        return (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {title}
          </a>
        );

      return <Link title={title} type url={url} />;
    },
  },
};

const RichText = ({ document }) => {
  if (document?.raw) return renderRichText(document, options);

  return null;
};

RichText.propTypes = {
  document: PropTypes.object,
  options: PropTypes.object,
};

export default RichText;
